/**
 * Who may see the demo tour (release 3 replica proof).
 *
 * The sales tour narrates a fictional showcase tenant ("IndiaPay Capital",
 * made-up findings, made-up request ids). It auto-opened on the landing page
 * for ANY first visit, so a customer's DPO signing in to their own install was
 * shown a demo of somebody else's data as if it were theirs.
 *
 * The product's own demo flag is the tenant's entitlements row:
 * `billing_status = 'demo'` marks the FOCTTA demo tenant and dev tenants
 * (migration 0041); every tenant the product provisions for a customer is
 * written `'active'`. So:
 *   - the sales tour (?demo=tour), its "Start guided demo tour" buttons and the
 *     first-visit auto-launch exist only for a tenant whose billing status is
 *     'demo';
 *   - anything else - a customer tenant, entitlements not loaded yet, a failed
 *     load, no one signed in - is treated as NOT a demo tenant. Fail closed: a
 *     customer never sees the demo because a request was slow.
 *   - the operator walkthrough (?demo=ops) is about the customer's own admin
 *     screens, so it stays available to everyone, but only when asked for; it
 *     never opens by itself.
 */

export type TourMode = 'sales' | 'ops';

/** True only for a tenant the product itself flags as a demo tenant. */
export function isDemoTenant(entitlements: { billingStatus?: string | null } | null | undefined): boolean {
  return entitlements?.billingStatus === 'demo';
}

/** May this tour mode be shown to this tenant? */
export function tourModeAllowed(mode: TourMode, demoTenant: boolean): boolean {
  return mode === 'ops' ? true : demoTenant;
}

/** Should the sales tour open by itself on this visit? */
export function shouldAutoLaunchTour(input: {
  demoTenant: boolean;
  tourActive: boolean;
  pathname: string;
  seenBefore: boolean;
}): boolean {
  return input.demoTenant && !input.tourActive && input.pathname === '/' && !input.seenBefore;
}
